import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  Button,
} from 'reactstrap';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from 'components/Common/Breadcrumb';
import { AddMedicineFormData, MedicineFormData } from '../medicineTypes';
import { BasicInfoForm } from './BasicInfoForm';
import { ClassificationForm } from './ClassificationForm';
import { InventoryPricingForm } from './InventoryPricingForm';
import { AdditionalDetailsForm } from './AdditionalDetailsForm';
import { useCreateMutation } from 'helpers/http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'helpers/http-client/httpConstants';
import ConfirmDetails from './ConfirmDetails';

const initialFormState: AddMedicineFormData = {
  name: '',
  dosageForm: '',
  medicineType: '',
  prescriptionRequired: false,
  isActive: true,
  printable: true,
  alias: '',
  genericName: '',
  strength: '',
  routeOfAdministration: '',
  storageConditions: '',
  shelfLifeMonths: undefined,
  approvalStatus: 'Pending',
  itemAlert: false,
  allowDue: false,
  generatePO: false,
  maxSaleDiscount: undefined,
  piecesInPacking: undefined,
  purchasePrice: undefined,
  salesPrice: undefined,
  retailPrice: undefined,
  packSalesTax: undefined,
  class: '',
  categoryId: '',
  manufacturerId: '',
  quantity: undefined,
  batchNumber: '',
  expiryDate: '',
  currency: '',
  country: '',
  medicineIngredients: [],
  medicineSideEffects: [],
};

const AddMedicineWizardForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isUrdu = i18n.language === 'ur' || i18n.dir() === 'rtl';

  const [formData, setFormData] = useState<AddMedicineFormData>(initialFormState);
  const [activeTab, setActiveTab] = useState<number>(1);
  const [passedSteps, setPassedSteps] = useState<number[]>([1]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { mutate: createMed } = useCreateMutation(API_ENDPOINTS.MEDICINES);

  const handleNext = (values: Partial<MedicineFormData>) => {
    setFormData((prev) => ({ ...prev, ...values }));
    goToNextTab();
  };

  const goToNextTab = () => {
    const nextTab = activeTab + 1;
    setPassedSteps((prev) => [...new Set([...prev, nextTab])]);
    setActiveTab(nextTab);
  };

  const handleSkip = () => {
    goToNextTab();
  };

  const handleSkipAll = () => {
    setPassedSteps([1, 2, 3, 4, 5]);
    setActiveTab(5);
  };

  const toggleTab = (tab: number) => {
    if (activeTab !== tab && tab >= 1 && tab <= 5 && passedSteps.includes(tab)) {
      setActiveTab(tab);
    }
  };

  const handleAddAnother = () => {
    setFormData(initialFormState);
    setActiveTab(1);
    setPassedSteps([1]);
    setIsSubmitted(false);
  };

  const handleSubmit = () => {
    createMed(formData, {
      onSuccess: () => {
        setIsSubmitted(true);
      },
    });
  };

  return (
    <React.Fragment>
      <div className="page-content" dir={isUrdu ? 'rtl' : 'ltr'}>
        <Container fluid={true}>
          <Breadcrumb title={t('Forms')} breadcrumbItem={t('Add New Medicine')} />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className={classnames('card-title mb-4', { 'fw-bold': isUrdu })}>
                    {t('Add New Medicine')}
                  </h4>
                  {isSubmitted ? (
                    <div className="text-center">
                      <Alert color="success" className={isUrdu ? 'fw-bold' : ''}>
                        <i className="mdi mdi-check-circle-outline me-2" />
                        {t('Medicine added successfully!')}
                      </Alert>
                      <p className={isUrdu ? 'fw-bold' : ''}>
                        {t('What would you like to do next?')}
                      </p>
                      <div className="d-flex justify-content-center gap-3">
                        <Button
                          color="primary"
                          onClick={handleAddAnother}
                          className={isUrdu ? 'fw-bold' : ''}
                        >
                          {t('Add Another Medicine')}
                        </Button>
                        <Button
                          color="secondary"
                          tag={Link}
                          to="/medicines-list"
                          className={isUrdu ? 'fw-bold' : ''}
                        >
                          {t('Go to Medicines List')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="wizard clearfix">
                      <div className="steps clearfix">
                        <ul>
                          <NavItem className={classnames({ current: activeTab === 1 })}>
                            <NavLink
                              onClick={() => toggleTab(1)}
                              disabled={!passedSteps.includes(1)}
                            >
                              <span className="number">1.</span> {t('Basic Info')}
                            </NavLink>
                          </NavItem>
                          <NavItem className={classnames({ current: activeTab === 2 })}>
                            <NavLink
                              onClick={() => toggleTab(2)}
                              disabled={!passedSteps.includes(2)}
                            >
                              <span className="number">2.</span> {t('Classification')}
                            </NavLink>
                          </NavItem>
                          <NavItem className={classnames({ current: activeTab === 3 })}>
                            <NavLink
                              onClick={() => toggleTab(3)}
                              disabled={!passedSteps.includes(3)}
                            >
                              <span className="number">3.</span> {t('Inventory & Pricing')}
                            </NavLink>
                          </NavItem>
                          <NavItem className={classnames({ current: activeTab === 4 })}>
                            <NavLink
                              onClick={() => toggleTab(4)}
                              disabled={!passedSteps.includes(4)}
                            >
                              <span className="number">4.</span> {t('Additional Details')}
                            </NavLink>
                          </NavItem>
                          <NavItem className={classnames({ current: activeTab === 5 })}>
                            <NavLink
                              onClick={() => toggleTab(5)}
                              disabled={!passedSteps.includes(5)}
                            >
                              <span className="number">5.</span> {t('Confirm Details')}
                            </NavLink>
                          </NavItem>
                        </ul>
                      </div>
                      <div className="content clearfix">
                        <TabContent activeTab={activeTab} className="body">
                          <TabPane tabId={1}>
                            <BasicInfoForm
                              formData={formData}
                              onNext={handleNext}
                              onSkip={handleSkip}
                              onSkipAll={handleSkipAll}
                              isUrdu={isUrdu}
                            />
                          </TabPane>
                          <TabPane tabId={2}>
                            <ClassificationForm
                              formData={formData}
                              onNext={handleNext}
                              onBack={() => setActiveTab(1)}
                              onSkip={handleSkip}
                              onSkipAll={handleSkipAll}
                              isUrdu={isUrdu}
                            />
                          </TabPane>
                          <TabPane tabId={3}>
                            <InventoryPricingForm
                              formData={formData}
                              onNext={handleNext}
                              onBack={() => setActiveTab(2)}
                              onSkip={handleSkip}
                              onSkipAll={handleSkipAll}
                              isUrdu={isUrdu}
                            />
                          </TabPane>
                          <TabPane tabId={4}>
                            <AdditionalDetailsForm
                              formData={formData}
                              onNext={handleNext}
                              onBack={() => setActiveTab(3)}
                              onSkip={handleSkip}
                              onSkipAll={handleSkipAll}
                              isUrdu={isUrdu}
                            />
                          </TabPane>
                          <TabPane tabId={5}>
                            <ConfirmDetails
                              formData={formData}
                              onBack={() => setActiveTab(4)}
                              onSubmit={handleSubmit}
                              isUrdu={isUrdu}
                            />
                          </TabPane>
                        </TabContent>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddMedicineWizardForm;
